import { Api, LicenseTester, Model, ModelRow, StringUtil, UserSettings } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    BlurEvent, Button, ChangeEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, DataSourceValidationEvent, Label, LookupModelSearchEvent, Snackbar, Textbox
} from "@mcleod/components";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { AutogenLayoutLMEUserIntegrations } from "./autogen/AutogenLayoutLMEUserIntegrations";
import { LMEUser } from "./LMEUser";

export class LMEUserIntegrations extends AutogenLayoutLMEUserIntegrations {
    private _datConnexionLoadboardService: boolean;
    private _hasDatOrgToken: boolean;
    private _hasDatRestService: boolean;
    private _rateviewEnabled: boolean;

    override async onLoad() {
        const isPnnLicensed = this.isPnnLicensed();
        if (this.isRateIndexLicensed() || isPnnLicensed) {
            const rateIndexResult = await Model.search("lme/powerbroker/rate-index-control", { vendor: "T" });
            const datServiceResult = isPnnLicensed ?
                await Model.search("lme/powerbroker/pbw-loadboard-services", { service_type: "T" }) :
                null;
            const datConnexionLoadboardService = isPnnLicensed ?
                await Model.search("lme/powerbroker/pbw-loadboard-services", { service_type: "C" }) :
                null;

            const rateIndexDatServiceRow = rateIndexResult?.modelRows[0] as ModelRow;
            this._rateviewEnabled = rateIndexDatServiceRow?.get("dat_rateview_enabled");
            this._hasDatRestService = StringUtil.equalsIgnoreCase(datServiceResult?.modelRows[0]?.get("is_active"),"Y");
            this._datConnexionLoadboardService =
                StringUtil.equalsIgnoreCase(datConnexionLoadboardService?.modelRows[0]?.get("is_active"),"Y");

            if (this._rateviewEnabled || this._hasDatRestService) {
                this.panelDATFields.visible = this._datConnexionLoadboardService && !this._hasDatRestService;
                const orgTokenSearchResult = await Model.search("lme/general/organization-token", { vendor: "T" });
                const rowOrgToken = orgTokenSearchResult?.modelRows[0] as ModelRow;
                this.panelRateViewDat.visible = true;
                this.imageDatLogo.visible = true;
                this._hasDatOrgToken = !StringUtil.isEmptyString(rowOrgToken?.get("organization_token"));
                this.textboxRateviewUserId.visible = this._hasDatOrgToken;
                this.buttonRateviewUserAuthorize.visible = this._hasDatOrgToken;

                if (this._hasDatOrgToken) {
                    await this.sourceLmeUserToken.search({ user_id: UserSettings.getUserId() });
                } else {
                    this.hideDatRestFields();
                }
            }
        }

        // Add the DataSourceMode change listener after the outermost layout has loaded.  That way it will be the last
        // mode change listener that will fire.  Specifically, we need it to fire after the DataHeader's mode change
        // listener, which rebuilds the SaveButton.
        this.addLayoutLoadOutermostListener(() => {
            this.sourceUserSettings.addAfterModeChangeListener(this.sourceUserSettingsAfterModeChange);
        });
    }

    private sourceUserSettingsAfterModeChange = (event: DataSourceModeChangeEvent) => {
        if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
            this.attachLmeUserTokenSourceToSaveButton();
        }
    }

    // sourceLmeUserToken is so weird...it needs to be posted, but only sometimes.  And it's a table designed to hold
    // multiple records, but the code is assuming it will only ever have one record for a user.  A seemingly better
    // option would be to use a repeater component to display fields for each integration separtely (and maybe show them
    // in a slideout and let their changes post right away?).
    private attachLmeUserTokenSourceToSaveButton() {
        // This is so gross
        if(this._rateviewEnabled || this._hasDatRestService) {
            const layoutLMEUser = this.getRootLayout() as LMEUser;
            layoutLMEUser.dataheaderLMEUser["saveButton"].extraDataSources = [ this.sourceLmeUserToken ];
        }
    }

    textboxTruckstopRESTServiceOnChange() {
        this.setButtonAuthorizeVisibility();
    }

    textboxTruckstopUserIdOnChange() {
        this.setButtonAuthorizeVisibility();
    }

    textboxTruckstopPasswordOnChange() {
        this.setButtonAuthorizeVisibility();
    }

    private setButtonAuthorizeVisibility() {
        this.buttonAuthorize.visible =
            !this.textboxTruckstopRESTService.isEmpty() &&
            !this.textboxTruckstopUserId.isEmpty() &&
            !this.textboxTruckstopPassword.isEmpty();
    }

    async buttonTruckstopAuthorizeOnClick() {
        this.labelTruckstopResult.imageColor = "primary";
        this.labelTruckstopResult.busy = true;

        const serviceId = this.textboxTruckstopRESTService.getDataValue();
        const userId = this.textboxTruckstopUserId.text;
        const password = this.textboxTruckstopPassword.text;

        if (StringUtil.isEmptyString(serviceId) ||
            StringUtil.isEmptyString(userId) ||
            StringUtil.isEmptyString(password)) {
            this.labelTruckstopResult.imageName = null;
            this.labelTruckstopResult.imageColor = "primary";
            this.labelTruckstopResult.busy = false;
            this.labelTruckstopResult.tooltip = null;
            Snackbar.showWarningSnackbar("Please provide all Truckstop REST fields (Service, Truckstop User ID, Password");
        } else {
            try {
                const response = await Api.post("lme/powerbroker/truckstop-user-auth", {
                    "service_id": serviceId,
                    "user_id": userId,
                    "password": password
                });
                this.labelTruckstopResult.busy = false;
                if (response.data[0].success) {
                    this.sourceUserSettings.activeRow.set("users_powerbroker.truckstop_access_token", response.data[0].access);
                    this.sourceUserSettings.activeRow.set("users_powerbroker.truckstop_refresh_token", response.data[0].refresh);
                    this.sourceUserSettings.activeRow.set("users_powerbroker.truckstop_token_expires", response.data[0].expire);

                    this.labelTruckstopResult.imageName = ImageName.circleCheck;
                    this.labelTruckstopResult.imageColor = "success";
                    this.textboxTruckstopPassword.clear();
                    this.labelTruckstopResult.tooltip = null;
                    Snackbar.showSnackbar("Success: Access tokens saved, Truckstop password not saved");
                } else {
                    this.labelTruckstopResult.imageName = ImageName.circleX;
                    this.labelTruckstopResult.imageColor = "error";
                    this.labelTruckstopResult.tooltip = response.data[0].error ? response.data[0].error : "There was an unexpected error Authorizing access.";
                }
            } finally {
                this.labelTruckstopResult.busy = false;
            }
        }
    }

    textboxRateviewUserIdOnBlur(event: BlurEvent){
        const textboxDatUserId = event.target as Textbox;
        if(StringUtil.isEmptyString(textboxDatUserId.text)){
            this.panelDatRestLoadboardSelection.visible = false;
            this.switchEnabledRateView.checked = false;
            this.switchLoadBoard.checked = false;
        }
    }

    textboxRateviewUserIdOnChange(event: ChangeEvent) {
        this.buttonRateviewUserAuthorize.visible = !StringUtil.isEmptyString(event.newValue);
        this.panelServiceOptions.visible = false;
        this.labelRateViewDATResult.visible = false;
        if (!this.buttonRateviewUserAuthorize.visible) {
            this.buttonRateviewUserAuthorize.tooltip = "Please provide a DAT User ID";
        } else {
            this.buttonRateviewUserAuthorize.tooltip = null;
        }
    }

    async buttonDatAuthorizeOnClick() {
        this.labelRateViewDATResult.visible = true;
        this.labelRateViewDATResult.imageColor = "primary";
        this.labelRateViewDATResult.busy = true;
        if (StringUtil.isEmptyString(this.textboxRateviewUserId.text)) {
            this.labelRateViewDATResult.imageName = null;
            this.labelRateViewDATResult.imageColor = "primary";
            this.labelRateViewDATResult.busy = false;
            this.labelRateViewDATResult.tooltip = null;
            Snackbar.showWarningSnackbar("Please provide DAT User ID");
        } else {
            try {
                const response = await Api.post("lme/general/rateindex/rateview/auth", {
                    "action_type": this._rateviewEnabled ? "U" : this._hasDatRestService ? "UL" : "",
                    "user_id": UserSettings.getUserId(),
                    "dat_rateview_user": this.textboxRateviewUserId.text
                });
                if(StringUtil.equalsIgnoreCase(response.data[0].dat_user_auth_error, "Authentication is blocked for one minute.")) {
                    Snackbar.showWarningSnackbar("Authentication is blocked for one minute.");
                }
                this.labelRateViewDATResult.busy = false;
                if (response?.data[0]?.is_valid_token) {
                    this.labelRateViewDATResult.tooltip = "User is successfully authenticated.";
                    this.labelRateViewDATResult.imageName = ImageName.circleCheck;
                    this.labelRateViewDATResult.imageColor = "success";
                    this.buttonRateviewUserAuthorize.visible = false;
                    this.switchEnabledRateView.checked = this._rateviewEnabled;
                    this.switchLoadBoard.checked = this._hasDatRestService;
                    this.panelServiceOptions.visible = true;
                } else {
                    this.labelRateViewDATResult.imageName = ImageName.circleX;
                    this.labelRateViewDATResult.imageColor = "error";
                    this.labelRateViewDATResult.tooltip = response?.data[0]?.dat_user_auth_error;
                    this.panelServiceOptions.visible = false;
                }
                await this.sourceLmeUserToken.search({user_id: UserSettings.getUserId()});
            } finally {
                this.labelRateViewDATResult.busy = false;
            }
        }
    }

    sourceUserSettingsAfterExecution() {
        if (this.sourceUserSettings.activeRow) {
            const [datUser, datPass, datError, truckstopTokensExist] = [
                this.sourceUserSettings.activeRow.get("users_powerbroker.dat_user_id"),
                this.sourceUserSettings.activeRow.get("users_powerbroker.dat_password"),
                this.sourceUserSettings.activeRow.get("users_powerbroker.dat_error"),
                this.sourceUserSettings.activeRow.get("users_powerbroker.truckstop_tokens_exist")
            ]

            if (!StringUtil.isEmptyString(datUser) && !StringUtil.isEmptyString(datPass)) {
                this.setDatStatusLabel(datError, this.labelDATResult, false,this.buttonAuthorize);
            }

            if (truckstopTokensExist) {
                this.labelTruckstopResult.imageName = ImageName.circleCheck;
                this.labelTruckstopResult.imageColor = "success";
            } else {
                this.labelTruckstopResult.imageName = ImageName.circleX;
                this.labelTruckstopResult.imageColor = "error";
                this.labelTruckstopResult.tooltip = "Tokens not set for this user";
            }
        }
    }

    sourceLmeUserTokenAfterExecution(event: DataSourceExecutionEvent){
        if (DataSourceAction.SEARCH == event.getAction()) {
            if (this.sourceLmeUserToken.activeRow) {
                const [datUserId, datRateviewUserAuthError, isValidToken] = [
                    this.sourceLmeUserToken.activeRow.get("dat_user_id"),
                    this.sourceLmeUserToken.activeRow.get("user_auth_error"),
                    this.sourceLmeUserToken.activeRow.get("is_valid_token")
                ]
                if (this._rateviewEnabled || this._hasDatRestService) {
                    this.buttonRateviewUserAuthorize.visible = !StringUtil.isEmptyString(datRateviewUserAuthError);

                    if (!StringUtil.isEmptyString(datUserId)) {
                        this.setDatStatusLabel(datRateviewUserAuthError, this.labelRateViewDATResult, !isValidToken, this.buttonRateviewUserAuthorize);
                        this.panelServiceOptions.visible = isValidToken;
                        this.setupRateViewFields();
                        this.setupDatRestFields(this._hasDatRestService && isValidToken);
                    }
                } else {
                    this.hideDatRestFields()
                }
            } else {
                this.panelServiceOptions.visible = false;
                //check if we have a dat user id in pnn transcore table
                Model.search("lme/powerbroker/pnn-transcore-user", {user_id: UserSettings.getUserId()}).then((response) => {
                    const datUserId = response?.modelRows[0]?.get("transcore_user_id");
                    if (!StringUtil.isEmptyString(datUserId)) {
                        this.textboxRateviewUserId.text = datUserId;
                    }
                });
            }
        }
    }

    private setupRateViewFields() {
        this.panelRateViewSelection.visible = this._rateviewEnabled;
        this.serviceSpacer.visible = this._rateviewEnabled;
    }

    private setupDatRestFields(loadboardDatRestEnabled: boolean) {
        this.panelDatRestLoadboardSelection.visible = loadboardDatRestEnabled && !StringUtil.isEmptyString(this.textboxRateviewUserId.text);
    }

    private hideDatRestFields() {
        this.buttonRateviewUserAuthorize.visible = false;
        this.textboxRateviewUserId.visible = false;
        this.labelRateViewDATResult.visible = false;
        this.panelRateViewDat.visible = false;
    }

    private isPnnLicensed(): boolean {
        return LicenseTester.testPackage("com.tms.client.loadmaster.pnn");
    }

    private isRateIndexLicensed(): boolean {
        return LicenseTester.testPackage("com.tms.client.loadmaster.rateindex");
    }

    private setDatStatusLabel(datError: string, label: Label, showUserAuthButton: boolean, buttonAuthorize: Button) {
        label.visible = true;
        buttonAuthorize.visible = showUserAuthButton;
        if (StringUtil.isEmptyString(datError) && !showUserAuthButton) {
            label.imageName = ImageName.circleCheck;
            label.imageColor = "success";
            label.tooltip = "User is successfully authenticated.";
        } else {
            label.imageName = ImageName.circleX;
            label.imageColor = "error";
            label.tooltip = datError;
        }
    }

    textboxDATServiceBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.service_type = 'C';
    }

    textboxTruckstopRESTServiceBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.service_type = 'R';
    }

    sourceUserSettingsOnValidate(event: DataSourceValidationEvent) {
        if (ValidationUtil.checkValidationResults(event.validationResults)) {
            this.clearDatFields();
        }
    }

    private clearDatFields() {
        if (this.sourceUserSettings.activeRow.get("users_powerbroker.loadboard_dat_id") == null) {
            this.sourceUserSettings.activeRow.set("users_powerbroker.dat_user_id", null);
            this.sourceUserSettings.activeRow.set("users_powerbroker.dat_password", null);
        }
    }
}
